import { Loader } from '@/components/layout/loading';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { setupOwnCloudFlow } from './SetupOwnCloudFlow';
import { type SetupOwnCloudState } from './SetupOwnCloudState';

export default function SetupOwnCloudWizard({
  tenantId,
  recalculateAccounts,
}: {
  tenantId: string;
  recalculateAccounts: () => void;
}) {
  const dal = useDAL();
  const { data: vaultAccounts, isLoading: isLoadingVaultAccounts } =
    dal.cloudAccounts.vault.list();
  const { data: scanningAccounts, isLoading: isLoadingScanningAccounts } =
    dal.cloudAccounts.scanning.list();
  const {
    data: azureBackupApplications,
    isLoading: isLoadingAzureBackupApplications,
  } = dal.onboarding.applications.get('Source');

  const { rightPanel } = useWorkspace();

  const onFinish = async () => {
    recalculateAccounts();
    return true;
  };

  if (
    isLoadingVaultAccounts ||
    isLoadingScanningAccounts ||
    isLoadingAzureBackupApplications
  ) {
    return <Loader />;
  }

  const hasVaultAccounts = !!vaultAccounts?.accounts.length;
  const hasScanningAccounts = !!scanningAccounts?.accounts.length;
  const { managementAppId, backupAppId, keyVaultAccessAppId } =
    azureBackupApplications || {};
  const hasAzureBackupApplications = !!backupAppId && !!managementAppId;

  const stepIndex = hasVaultAccounts
    ? 3
    : hasScanningAccounts
      ? 2
      : hasAzureBackupApplications
        ? 1
        : 0;

  return (
    <Wizard<SetupOwnCloudState>
      hideCloseButton={false}
      onAbort={() => rightPanel.setIsOpen(false)}
      hideBackArrow={false}
      onFinish={onFinish}
      flow={[setupOwnCloudFlow]}
      initialStep={{ stepIndex, keepPreviousSteps: true }}
      initialState={{
        onboardingFlow: true,
        tenantId,
        managementApplicationName: 'EonBackupApplication',
        regionName: 'eastus2',
        sourceConnectType: 'subscription',
        managementApplicationId: managementAppId || '',
        backupAppId,
        keyVaultApplicationId: keyVaultAccessAppId,
      }}
      title='Set up Eon on your own cloud'
      stepperLabels={[
        'Configure management access',
        'Set up scanning account',
        'Connect vault account',
        'Connect source account',
      ]}
      persistState={true}
      storageKey={`setup-own-cloud-${tenantId}`}
    />
  );
}
